import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, ArrowRight } from 'lucide-react';
import { FaLeaf, FaCut, FaTint, FaBug, FaSeedling, FaSync, FaClipboardList, FaSearch, FaCalendarAlt, FaChartLine, FaCheckCircle, FaWrench } from 'react-icons/fa';
import './ServiceDetail.css';

const ACCENT = '#1b5e20';
const ACCENT_LIGHT = '#4caf50';
const ACCENT_RGB = '27,94,32';

const benefits = [
  {
    emoji: <FaLeaf size={22} />,
    title: 'Regular Maintenance Visits',
    desc: 'Scheduled plant care visits to keep your indoor and outdoor plants healthy, fresh, and well-maintained throughout the year.'
  },
  {
    emoji: <FaCut size={22} />,
    title: 'Professional Pruning',
    desc: 'Expert trimming and shaping services that promote healthy growth and improve the appearance of your plants.'
  },
  {
    emoji: <FaTint size={22} />,
    title: 'Plant Health Monitoring',
    desc: 'Regular inspections to identify plant health issues early and ensure long-term growth and vitality.'
  },
  {
    emoji: <FaBug size={22} />,
    title: 'Pest & Disease Control',
    desc: 'Safe and effective treatments to protect plants from pests, diseases, and seasonal plant stress.'
  },
  {
    emoji: <FaSeedling size={22} />,
    title: 'Nutrition Management',
    desc: 'Proper fertilization and soil care programs designed to support stronger roots and healthier plants.'
  },
  {
    emoji: <FaSync size={22} />,
    title: 'Seasonal Plant Care',
    desc: 'Specialized maintenance and seasonal care to keep your green spaces attractive in every season.'
  },
];

const steps = [
  { num: '01', icon: <FaClipboardList size={20} />, title: 'Choose Your AMC Plan', desc: 'Select a plan based on your garden size and visit frequency.' },
  { num: '02', icon: <FaSearch size={20} />, title: 'Site Inspection', desc: 'Our experts assess your plants and create a personalized care schedule.' },
  { num: '03', icon: <FaCalendarAlt size={20} />, title: 'Regular Scheduled Visits', desc: 'Trained horticulturists arrive on schedule for hands-on care.' },
  { num: '04', icon: <FaChartLine size={20} />, title: 'Health Reports & Support', desc: 'Receive visit summaries and 24/7 advisory support anytime.' },
];

const stats = [
  { val: '100+', label: 'AMC Clients' },
  { val: '95%', label: 'Retention Rate' },
  { val: '3,000+', label: 'Plants Maintained' },
  { val: '3 Yrs', label: 'Of Expert Care' },
];

export default function AMCMaintenancePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'AMC Maintenance Service — Nisarg Nursery';
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sdp-observe').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="sdp-wrapper"
      style={{ '--accent': ACCENT, '--accent-light': ACCENT_LIGHT, '--accent-dark': '#1a3d1f', '--accent-rgb': ACCENT_RGB }}
    >
      {/* ════ HERO ════ */}
      <section className="sdp-hero">
        <div className="sdp-hero-bg">
          <img src="/images/flower5.jpg" alt="AMC Maintenance" className="sdp-hero-img" />
          <div className="sdp-hero-overlay" />
        </div>
        <div className="sdp-hero-content">
          <nav className="sdp-breadcrumb">
            <Link to="/">Home</Link> › <Link to="/services">Services</Link> › <span>AMC Maintenance</span>
          </nav>
          <div className="sdp-service-tag">
            <Wrench size={16} /> <FaLeaf /> Professional Green Care Service
          </div>
          <span className="sdp-hero-icon"><Wrench size={52} strokeWidth={1.5} /></span>
          <h1 className="sdp-hero-title">
            Managed <span className="accent-word">Plant Maintenance</span> For Every Green Space
          </h1>
          <p className="sdp-hero-desc">
            Keep your indoor plants, outdoor gardens, office greenery, and commercial landscapes healthy with expert plant care, regular maintenance visits, pruning, pest control, and seasonal plant management.
          </p>
          <div className="sdp-hero-cta">
            <Link to="/contact" className="sdp-btn-primary">
              Get AMC Quote <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="sdp-btn-outline">← Back to Services</Link>
          </div>
        </div>
        <div className="sdp-hero-scroll">
          <span>SCROLL</span>
          <div className="sdp-scroll-line" />
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="sdp-stats">
        {stats.map(s => (
          <div key={s.label} className="sdp-stat sdp-observe">
            <span className="sdp-stat-val">{s.val}</span>
            <span className="sdp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      <section className="sdp-section sdp-overview">
        <div className="sdp-section-inner">
          <div className="sdp-overview-grid">
            <div className="sdp-overview-left sdp-observe">
              <div className="sdp-overview-img-wrap">
                <img src="/images/chafa.jpg" alt="AMC Service" className="sdp-overview-img" />
                <div className="sdp-overview-badge">
                  <div className="sdp-overview-badge-dot" />
                  Active AMC Contracts
                </div>
              </div>
            </div>
            <div className="sdp-overview-right sdp-observe">
              <div className="sdp-section-tag"><FaSeedling /> Complete Plant Maintenance</div>
              <h2 className="sdp-section-title">Healthy Plants,<br />Beautiful Spaces</h2>
              <div className="sdp-overview-text">
                <p>Our AMC Maintenance Service ensures your plants remain healthy, vibrant, and visually appealing throughout the year. From homes and offices to hotels, hospitals, and commercial properties, we provide professional plant care tailored to your space.</p>
                <p>Our trained plant specialists handle cleaning, pruning, nutrition management, pest control, plant health monitoring, and seasonal maintenance, allowing you to enjoy greenery without the workload.</p>
              </div>
              <div className="sdp-features-list">
                {[
                  'Regular Visits',
                  'Plant Cleaning',
                  'Pruning Service',
                  'Pest Control',
                  'Health Monitoring',
                  'Seasonal Care'
                ].map(f => (
                  <div key={f} className="sdp-feature-item">
                    <div className="sdp-feature-icon"><FaCheckCircle /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaCheckCircle /> Service Highlights</div>
          <h2 className="sdp-section-title">Everything Your<br /> Plants Need</h2>
          <div className="sdp-benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{b.emoji}</span>
                <h3 className="sdp-benefit-title">{b.title}</h3>
                <p className="sdp-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="sdp-section sdp-process sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaSync /> How It Works</div>
          <h2 className="sdp-section-title">Our Maintenance Process</h2>
          <div className="sdp-process-steps">
            {steps.map(s => (
              <div key={s.num} className="sdp-step sdp-observe">
                <div className="sdp-step-num">{s.num}</div>
                <div className="sdp-step-icon">{s.icon}</div>
                <h3 className="sdp-step-title">{s.title}</h3>
                <p className="sdp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="sdp-cta sdp-observe">
        <span className="sdp-cta-icon"><FaWrench size={28} /></span>
        <h2 className="sdp-cta-title">Let's Maintain Your Green Space</h2>
        <p className="sdp-cta-desc">Contact us today and get a customised AMC plan for your home, office, or property.</p>
        <div className="sdp-cta-btns">
          <Link to="/contact" className="sdp-btn-primary">
            Get a Custom Plan
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="sdp-btn-outline">← All Services</Link>
        </div>
      </div>
    </div>
  );
}
